// A floating readout of what has been taken off: per condition, the totals on THIS drawing and on ALL drawings.
// Computed from the rows the canvas already holds with the canvas's own quantity function, so it always agrees with
// the labels and the Conditions dock. Conditions hidden with the eye are not counted here either.
// Shown in the viewer's display units (metric: m, m², m³).
import { useUnits } from "../../utils/useUnits";
import { isMetric, fmtQty } from "../../utils/units";

const fmtQ = (q) => (Math.round(q * 10) / 10).toLocaleString();
const text = (totals, units) => totals.map(({ u, q }) => (isMetric(units) ? fmtQty({ q, u }, units) : `${fmtQ(q)} ${u}`)).join(" / ") || "—";

export default function LiveCounter({ here, all, onClose }) {
  const units = useUnits();
  const allByKey = new Map(all.map(g => [g.key, g]));
  const keys = [...new Set([...here.map(g => g.key), ...all.map(g => g.key)])];
  const byKey = new Map([...all, ...here].map(g => [g.key, g]));
  return (
    <div role="region" aria-label="Takeoff totals" data-testid="live-counter"
      style={{ position: "absolute", top: 8, right: 8, zIndex: 6, minWidth: 260, maxWidth: 360, maxHeight: "60%", overflowY: "auto", background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.2)", fontSize: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", borderBottom: "1px solid var(--cs-border)" }}>
        <b style={{ fontSize: 12 }}>Totals</b>
        <button onClick={onClose} aria-label="Close totals" style={{ border: "none", background: "none", cursor: "pointer", color: "var(--text3)", fontSize: 12 }}>✕</button>
      </div>
      {keys.length === 0 ? (
        <div style={{ padding: "10px", color: "var(--text3)" }}>Nothing taken off yet.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ color: "var(--text3)", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>
              <th style={{ textAlign: "left", padding: "4px 10px", fontWeight: 700 }}>Condition</th>
              <th style={{ textAlign: "right", padding: "4px 6px", fontWeight: 700 }}>This drawing</th>
              <th style={{ textAlign: "right", padding: "4px 10px", fontWeight: 700 }}>All drawings</th>
            </tr>
          </thead>
          <tbody>
            {keys.map(k => {
              const g = byKey.get(k);
              const h = here.find(x => x.key === k);
              return (
                <tr key={k} style={{ borderTop: "1px solid var(--cs-border)" }}>
                  <td style={{ padding: "4px 10px" }}>
                    {g.color && <span aria-hidden="true" style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: g.color, border: "1px solid var(--cs-border)", marginRight: 6 }} />}
                    {g.label}
                  </td>
                  <td style={{ padding: "4px 6px", textAlign: "right", fontFamily: "monospace" }}>{h ? text(h.totals, units) : "—"}</td>
                  <td style={{ padding: "4px 10px", textAlign: "right", fontFamily: "monospace" }}>{allByKey.has(k) ? text(allByKey.get(k).totals, units) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
